import { List, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { AppService, DomainTypes, Types } from '../../DomainTypes';
import { ConfigValues } from '../../Models/Config/TenantConfig';
import { NotificationDuration } from '../../../App/Models/NotificationDuration';
import FieldConfigValues from '../../Models/Config/FieldConfig/FieldConfigValues';
import { NotificationSettingsVM } from "@singularsystems/neo-notifications";
import CorrespondenceConfigValues from "../../Models/Config/CorrespondenceConfig";
import { ExportConfig } from "../../Models/Config/ExportConfig";
import { ReportingConfigValues } from "../../Models/Config/ReportingConfig";
import ImportConfig from '../../Models/Import/ImportConfig';
import TextValues from '../../Services/TextValues';

@NeoModel
export default class ConfigVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private apiClient = AppService.get(Types.Domain.ApiClients.Config),
        private importApiClient = AppService.get(Types.Domain.ApiClients.Import),
        private dataCache = AppService.get(DomainTypes.Services.DataCache)) {
        super(taskRunner);

    }

    public notificationsVM = this.registerViewModel(NotificationSettingsVM);

    public selectedTab = "System Config";
    public config: ConfigValues | null = null;
    public reportingConfig: ReportingConfigValues | null = null;
    public fieldConfig: FieldConfigValues | null = null;
    public correspondenceConfig: CorrespondenceConfigValues | null = null;
    public exportConfig: ExportConfig | null = null;
    public importConfigs = new List(ImportConfig);

    public async initialise() {

    }

    public async loadConfig() {
        const response = await this.taskRunner.waitFor(this.apiClient.getConfig());
        this.config = ConfigValues.fromJSObject<ConfigValues>(response.data)
    }

    public async loadFieldConfig() {
        await this.taskRunner.waitFor(this.dataCache.loadFieldConfig())
        this.fieldConfig = this.dataCache.fieldConfig.clone();
    }

    public async loadReportingConfig() {
        const response = await this.taskRunner.waitFor(this.apiClient.getReportingConfig());
        const reportingConfig = ReportingConfigValues.fromJSObject<ReportingConfigValues>(response.data);
        reportingConfig.markOld();
        this.reportingConfig = reportingConfig;
    }

    public async loadCorrespondenceConfig() {
        const data = await this.taskRunner.waitForData(this.apiClient.getCorrespondenceConfig());
        const correspondenceConfig = CorrespondenceConfigValues.fromJSObject<CorrespondenceConfigValues>(data);
        correspondenceConfig.markOld();
        this.correspondenceConfig = correspondenceConfig;
    }

    public async loadExportConfig() {
        const data = await this.taskRunner.waitForData(this.apiClient.getExportConfig());
        const exportConfig = ExportConfig.fromJSObject<ExportConfig>(data);
        exportConfig.markOld();

        this.exportConfig = exportConfig;
    }

    public async loadImportConfig() {
        const data = await this.taskRunner.waitForData(this.importApiClient.getImportConfigs());
        this.importConfigs.set(data);
    }

    public updateConfig() {
        this.taskRunner.run(async () => {
            const result = await this.apiClient.updateConfig(this.config!.toJSObject());
            this.config!.set(result.data);
            this.config!.markOld();
            TextValues.updateFromConfig(this.config!);
            this.dataCache.tenantConfig.expire();
            NotifyUtils.addSuccess("Saved", "Settings were saved successfully.", NotificationDuration.Standard);
        });
    }

    public updateReportingConfig() {
        this.taskRunner.run(async () => {
            await this.apiClient.updateReportingConfig(this.reportingConfig!.toJSObject());
            this.reportingConfig!.markOld();
            NotifyUtils.addSuccess("Saved", "Reporting settings were saved successfully.", NotificationDuration.Standard);
        });
    }

    public updateFieldConfig() {
        this.taskRunner.run(async () => {
            await this.apiClient.updateFieldConfig(this.fieldConfig!.toJSObject());
            await this.taskRunner.waitFor(this.dataCache.loadFieldConfig())
            this.fieldConfig = this.dataCache.fieldConfig.clone();
            NotifyUtils.addSuccess("Saved", "Field settings were saved successfully.", NotificationDuration.Standard);
        });
    }

    public async updateCorrespondenceConfig() {
        await this.taskRunner.run(() => this.apiClient.updateCorrespondenceConfig(this.correspondenceConfig!.toJSObject()));
        this.correspondenceConfig!.markOld();
        NotifyUtils.addSuccess("Saved", "Correspondence settings were saved successfully.", NotificationDuration.Standard);
    }

    public async updateExportConfig() {
        await this.taskRunner.run(() => this.apiClient.updateExportConfig(this.exportConfig!.toJSObject()));
        this.exportConfig!.markOld();
        NotifyUtils.addSuccess("Saved", "Export settings were saved successfully.", NotificationDuration.Standard);
    }

    public async updateImportConfigs() {
        this.taskRunner.run(async () => {
            const response = await this.importApiClient.saveImportConfigs(this.importConfigs!.toJSArray());
            this.importConfigs.update(response.data);
            this.dataCache.importConfig.expire();
            NotifyUtils.addSuccess("Saved", "Import settings were saved successfully.", NotificationDuration.Standard);
        });
    }
}